import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { IExperimentsState, IPromptSlot } from './experiments.props';
import {
  buildUpsellExperimentStamp,
  currentCarSessionId,
  getSpokenPrompt,
  toConstraintKey,
  toPromptSlotKey,
} from './experiments.utils';

const EMPTY_EXPERIMENTS: IExperimentsState = {
  bySession: {},
  sessionOrder: [],
};
const EMPTY_PROMPTS: Record<string, string> = {};
const EMPTY_ASK_COUNTS: Record<string, number> = {};
const EMPTY_NAMES: string[] = [];

const selectExperiments = (state: RootState) =>
  state.experiments ?? EMPTY_EXPERIMENTS;

const selectCurrentCarSessionId = (state: RootState) =>
  currentCarSessionId({
    currentTaskId: state.taskRouter?.currentTaskId,
    taskAssignedSessionId: state.taskRouter?.taskAssignedSessionId,
    sessionBoundarySessionId: state.sessionBoundary?.sessionId,
  });

const selectCurrentCarExperiments = (state: RootState) =>
  selectExperiments(state).bySession[selectCurrentCarSessionId(state)];

// Null when nothing was received for the car the console is working.
const selectExperimentAssignment = (state: RootState) =>
  selectCurrentCarExperiments(state)?.assignment ?? null;

// Lowercase names, as carried on cart action payloads.
const selectWithheldConstraintNames = createSelector(
  selectExperimentAssignment,
  (assignment) =>
    assignment?.withheld_constraints?.map(toConstraintKey) ?? EMPTY_NAMES
);

const selectWithheldConstraints = createSelector(
  selectWithheldConstraintNames,
  (names) => new Set(names)
);

const selectPromptOverrides = createSelector(
  selectExperimentAssignment,
  (assignment) => assignment?.prompts ?? EMPTY_PROMPTS
);

const selectPromptAskCounts = (state: RootState) =>
  selectCurrentCarExperiments(state)?.promptAskCounts ?? EMPTY_ASK_COUNTS;

const selectPromptAskCount = (state: RootState, slot: IPromptSlot) =>
  selectPromptAskCounts(state)[toPromptSlotKey(slot)] ?? 0;

// The phrase a slot's next ask speaks; see getSpokenPrompt.
const selectSpokenPrompt = (
  state: RootState,
  slot: IPromptSlot,
  menuPrompt: string | string[] | undefined
) =>
  getSpokenPrompt(
    selectPromptOverrides(state),
    slot.constraintName,
    menuPrompt,
    selectPromptAskCount(state, slot)
  );

const selectUpsellExperimentStamp = (
  state: RootState,
  constraintName: string | null | undefined
) =>
  buildUpsellExperimentStamp(selectExperimentAssignment(state), constraintName);

export {
  selectCurrentCarSessionId,
  selectExperimentAssignment,
  selectWithheldConstraintNames,
  selectWithheldConstraints,
  selectPromptOverrides,
  selectPromptAskCounts,
  selectPromptAskCount,
  selectSpokenPrompt,
  selectUpsellExperimentStamp,
};
